/**
 * shadcn-style toast hook, backed by sonner.
 *
 * This used to call react-toastify, which is not a dependency of this app (it
 * only resolved because @team-plain/react-chat-ui pulls it in transitively) and
 * whose ToastContainer was never mounted — so every call silently rendered
 * nothing. It now forwards to sonner, whose <Toaster /> is mounted once at the
 * root (components/ui/sonner.tsx, main.tsx).
 *
 * Call shape is unchanged: toast({ title, description, variant }).
 * A call with no variant is a success confirmation (what every existing call
 * site without one means); `variant: 'default'` is a neutral message.
 */

import type { ReactNode } from 'react';
import { toast as sonner, type ExternalToast } from 'sonner';

export type ToastVariant = 'default' | 'success' | 'destructive' | 'error' | 'warning' | 'info';

export interface ToastProps {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  /** Milliseconds; sonner's default (4s) when omitted. Errors default to 8s. */
  duration?: number;
  /** Optional inline action button, e.g. "Open task". */
  action?: { label: ReactNode; onClick: () => void };
  id?: string | number;
}

export function toast({ title, description, variant, duration, action, id }: ToastProps): string | number {
  // sonner needs a message; fall back to the description when only that is given.
  const message = title ?? description ?? '';
  const options: ExternalToast = {
    description: title != null ? description : undefined,
    action: action ? { label: action.label, onClick: () => action.onClick() } : undefined,
    id,
  };
  const isError = variant === 'destructive' || variant === 'error';
  const effectiveDuration = duration ?? (isError ? 8000 : undefined);
  if (effectiveDuration !== undefined) options.duration = effectiveDuration;

  switch (variant) {
    case 'destructive':
    case 'error':
      return sonner.error(message, options);
    case 'warning':
      return sonner.warning(message, options);
    case 'info':
      return sonner.info(message, options);
    case 'default':
      return sonner(message, options);
    case 'success':
    default:
      return sonner.success(message, options);
  }
}

export function useToast() {
  return { toast, dismiss: (id?: string | number) => sonner.dismiss(id) };
}

export { useToast as default };
